import React from "react";
import { Dropdown } from "react-bootstrap";
import {
  FaTachometerAlt,
  FaUser,
  FaMusic,
  FaCompactDisc,
  FaEllipsisH,
  FaListUl,
  FaQuestionCircle,
  FaTimes,
  FaTags,
  FaUserShield,
  FaThLarge,
  FaNewspaper,
  FaChartLine,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { ROUTE_CONSTANT } from "../../routes/constant";
import { usePermission } from "../../context/PermissionContext";
import "./Sidebar.css";

const Sidebar = ({ isCollapsed, isMobileOpen, onClose, isMobileView }) => {
  const navigate = useNavigate();
  const { isSuperAdmin, hasAnyPermission } = usePermission();

  // Handle navigation and close sidebar on mobile
  const handleNavClick = (path) => (e) => {
    if (isMobileView && isMobileOpen) {
      e.preventDefault();
      onClose();
      // Small delay to allow animation to complete
      setTimeout(() => {
        navigate(path);
      }, 150);
    }
  };

  // Permission check helpers
  const canAccessArtist = () => hasAnyPermission(['artist:view', 'artist:create', 'artist:edit', 'artist:delete']);
  const canAccessAlbum = () => hasAnyPermission(['album:view', 'album:create', 'album:edit', 'album:delete']);
  const canAccessSong = () => hasAnyPermission(['song:view', 'song:create', 'song:edit', 'song:delete']);
  const canAccessPlaylist = () => hasAnyPermission(['playlist:view', 'playlist:create', 'playlist:edit', 'playlist:delete']);
  const canAccessQuiz = () => hasAnyPermission(['quiz:view', 'quiz:create', 'quiz:edit', 'quiz:delete']);
  const canAccessGenre = () => hasAnyPermission(['genre:view', 'genre:create', 'genre:edit', 'genre:delete']);
  const canAccessNews = () => hasAnyPermission(['news:view', 'news:create', 'news:edit', 'news:delete']);
  const canAccessPageManagement = () => isSuperAdmin() || canAccessGenre() || canAccessNews();
  const canAccessAuthors = () => canAccessNews();
  const canAccessContact = () => hasAnyPermission(['contact:view', 'contact:manage']);
  const canAccessSubscribers = () => hasAnyPermission(['subscribers:view', 'subscribers:manage']);
  const canAccessYoutube = () => hasAnyPermission(['youtube:view', 'youtube:manage']);
  const canAccessPodcast = () => hasAnyPermission(['podcast:view', 'podcast:manage']);
  const canAccessCache = () => hasAnyPermission(['cache:view', 'cache:manage']);
  const canAccessTags = () => hasAnyPermission(['tags:view', 'tags:manage']);
  const canAccessUserActivity = () => hasAnyPermission(['user-activity:view']);
  const canAccessAnalytics = () => hasAnyPermission(['analytics:read']);
  const canAccessNotification = () => hasAnyPermission(['notification:view', 'notification:create', 'notification:delete']);
  // Contribution permissions (include legacy 'contributions:view/manage' for backward compatibility)
  const canAccessSongLyricsContributions = () => hasAnyPermission(['song-lyrics-contributions:view', 'song-lyrics-contributions:manage', 'contributions:view', 'contributions:manage']);
  const canAccessAlbumContributions = () => hasAnyPermission(['album-contributions:view', 'album-contributions:manage', 'contributions:view', 'contributions:manage']);
  const canAccessSongContributions = () => hasAnyPermission(['song-contributions:view', 'song-contributions:manage', 'contributions:view', 'contributions:manage']);
  const canAccessArtistBioContributions = () => hasAnyPermission(['artist-bio-contributions:view', 'artist-bio-contributions:manage', 'contributions:view', 'contributions:manage']);
  const canAccessVideoContributions = () => hasAnyPermission(['video-contributions:view', 'video-contributions:manage', 'contributions:view', 'contributions:manage']);

  // Check if "Other" dropdown should be visible (has at least one accessible item)
  const canAccessOther = () =>
    canAccessArtist() || // Artist Bio
    canAccessAuthors() ||
    canAccessNews() ||
    canAccessSongLyricsContributions() ||
    canAccessAlbumContributions() ||
    canAccessSongContributions() ||
    canAccessArtistBioContributions() ||
    canAccessVideoContributions() ||
    canAccessContact() ||
    canAccessSubscribers() ||
    canAccessYoutube() ||
    canAccessPodcast() ||
    canAccessUserActivity() ||
    canAccessAnalytics() ||
    canAccessCache() ||
    canAccessTags() ||
    canAccessNotification();

  // On desktop: sidebar visible by default, isCollapsed=true means hidden
  // On mobile: sidebar hidden by default, isMobileOpen=true means visible
  const isDesktopVisible = !isMobileView && !isCollapsed;
  const isMobileVisible = isMobileView && isMobileOpen;
  const isVisible = isDesktopVisible || isMobileVisible;

  return (
    <nav
      className={`sidebar ${isCollapsed ? "sidebar-collapsed" : "sidebar-expanded"} ${isMobileOpen ? "sidebar-mobile-open" : ""}`}
      style={{
        zIndex: 1050,
        position: "fixed",
        left: 0,
        top: 0,
        width: isVisible
          ? (isMobileView ? "var(--sidebar-width-mobile)" : "var(--sidebar-width-desktop)")
          : "0px",
        height: "100vh",
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        opacity: isVisible ? 1 : 0,
        visibility: isVisible ? "visible" : "hidden",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <div className="sidebar-header p-3 d-flex justify-content-between align-items-center">
        <span className="sidebar-brand">Lyrics Web</span>
        {isMobileOpen && (
          <button
            className="sidebar-close-btn"
            onClick={onClose}
            aria-label="Close sidebar"
          >
            <FaTimes size={18} />
          </button>
        )}
      </div>

        <div
          className={`sidebar-nav d-flex flex-column my-4 g-4 ${isCollapsed ? "collapsed" : ""}`}
        >
          {/* Dashboard - Always visible */}
          <Link
            to="/"
            className="sidebar-link"
            onClick={handleNavClick("/")}
          >
            <FaTachometerAlt className="me-2" size={20} /> Dashboard
          </Link>

          {/* Artist Dropdown */}
          {canAccessArtist() && (
            <Dropdown className="sidebar-dropdown">
              <Dropdown.Toggle className="sidebar-dropdown-toggle">
                <FaUser className="me-2" size={20} /> Artist
              </Dropdown.Toggle>

              <Dropdown.Menu className="sidebar-dropdown-menu">
                <Dropdown.Item as={Link} to="/add-artist" className="sidebar-dropdown-item" onClick={handleNavClick("/add-artist")}>
                  Add Artist
                </Dropdown.Item>
                <Dropdown.Item as={Link} to={ROUTE_CONSTANT.COMMON.LIST_ARTISTS} className="sidebar-dropdown-item" onClick={handleNavClick(ROUTE_CONSTANT.COMMON.LIST_ARTISTS)}>
                  All Artists
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/trending-artist" className="sidebar-dropdown-item" onClick={handleNavClick("/trending-artist")}>
                  Trending Artist
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/recommended-artist" className="sidebar-dropdown-item" onClick={handleNavClick("/recommended-artist")}>
                  Recommended Artist
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/top-artist" className="sidebar-dropdown-item" onClick={handleNavClick("/top-artist")}>
                  Top Artist
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}

          {/* Album Dropdown */}
          {canAccessAlbum() && (
            <Dropdown className="sidebar-dropdown">
              <Dropdown.Toggle className="sidebar-dropdown-toggle">
                <FaCompactDisc className="me-2" size={20} /> Album
              </Dropdown.Toggle>
              <Dropdown.Menu className="sidebar-dropdown-menu">
                <Dropdown.Item as={Link} to={ROUTE_CONSTANT.COMMON.ADD_ALBUM} className="sidebar-dropdown-item" onClick={handleNavClick(ROUTE_CONSTANT.COMMON.ADD_ALBUM)}>
                  Add Album
                </Dropdown.Item>
                <Dropdown.Item as={Link} to={ROUTE_CONSTANT.COMMON.TOP_EXPLORE} className="sidebar-dropdown-item" onClick={handleNavClick(ROUTE_CONSTANT.COMMON.TOP_EXPLORE)}>
                  Top Explore
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}

          {/* Song Dropdown */}
          {canAccessSong() && (
            <Dropdown className="sidebar-dropdown">
              <Dropdown.Toggle className="sidebar-dropdown-toggle">
                <FaMusic className="me-2" size={20} /> Song
              </Dropdown.Toggle>
              <Dropdown.Menu className="sidebar-dropdown-menu">
                <Dropdown.Item as={Link} to={ROUTE_CONSTANT.COMMON.LIST_SONGS} className="sidebar-dropdown-item" onClick={handleNavClick(ROUTE_CONSTANT.COMMON.LIST_SONGS)}>
                  All Songs
                </Dropdown.Item>
                <Dropdown.Item as={Link} to={ROUTE_CONSTANT.COMMON.ADD_SONG} className="sidebar-dropdown-item" onClick={handleNavClick(ROUTE_CONSTANT.COMMON.ADD_SONG)}>
                  Add New Song
                </Dropdown.Item>
                <Dropdown.Item as={Link} to={ROUTE_CONSTANT.COMMON.RECOMMENDED_SONGS} className="sidebar-dropdown-item" onClick={handleNavClick(ROUTE_CONSTANT.COMMON.RECOMMENDED_SONGS)}>
                  Recommended Song
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/popular_songs" className="sidebar-dropdown-item" onClick={handleNavClick("/popular_songs")}>
                  Most Popular Song
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/top-songs" className="sidebar-dropdown-item" onClick={handleNavClick("/top-songs")}>
                  Top Song
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/new-release-songs" className="sidebar-dropdown-item" onClick={handleNavClick("/new-release-songs")}>
                  New Release Songs
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}

          {/* Playlist Dropdown */}
          {canAccessPlaylist() && (
            <Dropdown className="sidebar-dropdown">
              <Dropdown.Toggle className="sidebar-dropdown-toggle">
                <FaListUl className="me-2" size={20} /> Playlist
              </Dropdown.Toggle>
              <Dropdown.Menu className="sidebar-dropdown-menu">
                <Dropdown.Item as={Link} to={ROUTE_CONSTANT.COMMON.CREATE_PLAYLIST} className="sidebar-dropdown-item" onClick={handleNavClick(ROUTE_CONSTANT.COMMON.CREATE_PLAYLIST)}>
                  Add Playlist
                </Dropdown.Item>
                <Dropdown.Item as={Link} to={ROUTE_CONSTANT.COMMON.PLAYLIST} className="sidebar-dropdown-item" onClick={handleNavClick(ROUTE_CONSTANT.COMMON.PLAYLIST)}>
                  Get Playlist
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}

          {/* Quiz Dropdown */}
          {canAccessQuiz() && (
            <Dropdown className="sidebar-dropdown">
              <Dropdown.Toggle className="sidebar-dropdown-toggle">
                <FaQuestionCircle className="me-2" size={20} /> Quiz
              </Dropdown.Toggle>
              <Dropdown.Menu className="sidebar-dropdown-menu">
                <Dropdown.Item as={Link} to={ROUTE_CONSTANT.COMMON.QUIZ} className="sidebar-dropdown-item" onClick={handleNavClick(ROUTE_CONSTANT.COMMON.QUIZ)}>
                  Quiz List
                </Dropdown.Item>
                <Dropdown.Item as={Link} to={ROUTE_CONSTANT.COMMON.ADD_QUIZ} className="sidebar-dropdown-item" onClick={handleNavClick(ROUTE_CONSTANT.COMMON.ADD_QUIZ)}>
                  Add Quiz
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}

          {/* Genres Link */}
          {canAccessGenre() && (
            <Link
              to="/genres"
              className="sidebar-btn-link"
              onClick={handleNavClick("/genres")}
            >
              <FaTags className="me-2" size={20} /> Genres
            </Link>
          )}

          {canAccessNews() && (
            <Link
              to={ROUTE_CONSTANT.COMMON.NEWSROOM_CONTROL}
              className="sidebar-btn-link"
              onClick={handleNavClick(ROUTE_CONSTANT.COMMON.NEWSROOM_CONTROL)}
            >
              <FaNewspaper className="me-2" size={20} /> Newsroom
            </Link>
          )}

          {canAccessPageManagement() && (
            <Link
              to={ROUTE_CONSTANT.COMMON.PAGE_MANAGEMENT}
              className="sidebar-btn-link"
              onClick={handleNavClick(ROUTE_CONSTANT.COMMON.PAGE_MANAGEMENT)}
            >
              <FaThLarge className="me-2" size={20} /> Page Management
            </Link>
          )}

          {/* Admin Management Link - only visible to superadmins */}
          {isSuperAdmin() && (
            <Link
              to={ROUTE_CONSTANT.COMMON.ADMIN_MANAGEMENT}
              className="sidebar-btn-link"
              onClick={handleNavClick(ROUTE_CONSTANT.COMMON.ADMIN_MANAGEMENT)}
            >
              <FaUserShield className="me-2" size={20} /> Admin Users
            </Link>
          )}

          {/* Other Dropdown */}
          {canAccessOther() && (
            <Dropdown className="sidebar-dropdown" drop="up">
              <Dropdown.Toggle className="sidebar-dropdown-toggle">
                <FaEllipsisH className="me-2" size={20} /> Other
              </Dropdown.Toggle>
              <Dropdown.Menu className="sidebar-dropdown-menu sidebar-dropdown-scroll">
                {canAccessArtist() && (
                  <Dropdown.Item as={Link} to="/artist-bio" className="sidebar-dropdown-item" onClick={handleNavClick("/artist-bio")}>
                    Artist Bio
                  </Dropdown.Item>
                )}
                {canAccessNews() && (
                  <Dropdown.Item as={Link} to="/news" className="sidebar-dropdown-item" onClick={handleNavClick("/news")}>
                    News
                  </Dropdown.Item>
                )}
                {canAccessAuthors() && (
                  <Dropdown.Item
                    as={Link}
                    to={ROUTE_CONSTANT.COMMON.AUTHOR_MANAGEMENT}
                    className="sidebar-dropdown-item"
                    onClick={handleNavClick(ROUTE_CONSTANT.COMMON.AUTHOR_MANAGEMENT)}
                  >
                    Author Management
                  </Dropdown.Item>
                )}
                {canAccessSongLyricsContributions() && (
                  <Dropdown.Item as={Link} to="/song-lyrics-contributions" className="sidebar-dropdown-item" onClick={handleNavClick("/song-lyrics-contributions")}>
                    Song Lyrics Contributions
                  </Dropdown.Item>
                )}
                {canAccessAlbumContributions() && (
                  <Dropdown.Item as={Link} to="/album-contributions" className="sidebar-dropdown-item" onClick={handleNavClick("/album-contributions")}>
                    Album Contributions
                  </Dropdown.Item>
                )}
                {canAccessSongContributions() && (
                  <Dropdown.Item as={Link} to="/song-contributions" className="sidebar-dropdown-item" onClick={handleNavClick("/song-contributions")}>
                    Song Contributions
                  </Dropdown.Item>
                )}
                {canAccessArtistBioContributions() && (
                  <Dropdown.Item as={Link} to="/artist-bio-contributions" className="sidebar-dropdown-item" onClick={handleNavClick("/artist-bio-contributions")}>
                    Artist Bio Contributions
                  </Dropdown.Item>
                )}
                {canAccessVideoContributions() && (
                  <Dropdown.Item as={Link} to="/video-contributions" className="sidebar-dropdown-item" onClick={handleNavClick("/video-contributions")}>
                    Video Contributions
                  </Dropdown.Item>
                )}
                {canAccessContact() && (
                  <Dropdown.Item as={Link} to="/contact-us" className="sidebar-dropdown-item" onClick={handleNavClick("/contact-us")}>
                    Contact Us
                  </Dropdown.Item>
                )}
                {canAccessSubscribers() && (
                  <Dropdown.Item as={Link} to="/news-subscriber" className="sidebar-dropdown-item" onClick={handleNavClick("/news-subscriber")}>
                    News Subscriber
                  </Dropdown.Item>
                )}
                {canAccessYoutube() && (
                  <Dropdown.Item as={Link} to="/youtube-videos" className="sidebar-dropdown-item" onClick={handleNavClick("/youtube-videos")}>
                    Youtube Videos
                  </Dropdown.Item>
                )}
                {canAccessPodcast() && (
                  <Dropdown.Item as={Link} to="/podcast-section" className="sidebar-dropdown-item" onClick={handleNavClick("/podcast-section")}>
                    Podcast Section
                  </Dropdown.Item>
                )}
                {canAccessUserActivity() && (
                  <Dropdown.Item as={Link} to="/user-activity" className="sidebar-dropdown-item" onClick={handleNavClick("/user-activity")}>
                    User Activity
                  </Dropdown.Item>
                )}
                {canAccessAnalytics() && (
                  <Dropdown.Item
                    as={Link}
                    to={ROUTE_CONSTANT.COMMON.ENGAGEMENT_ANALYTICS}
                    className="sidebar-dropdown-item"
                    onClick={handleNavClick(ROUTE_CONSTANT.COMMON.ENGAGEMENT_ANALYTICS)}
                  >
                    <FaChartLine className="me-2" /> Engagement Analytics
                  </Dropdown.Item>
                )}
                {canAccessCache() && (
                  <>
                    <Dropdown.Item as={Link} to="/cache-management" className="sidebar-dropdown-item" onClick={handleNavClick("/cache-management")}>
                      Cache Management
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/content-prewarming" className="sidebar-dropdown-item" onClick={handleNavClick("/content-prewarming")}>
                      Content Pre-warming
                    </Dropdown.Item>
                  </>
                )}
                {canAccessTags() && (
                  <Dropdown.Item as={Link} to="/tags" className="sidebar-dropdown-item" onClick={handleNavClick("/tags")}>
                    Tags
                  </Dropdown.Item>
                )}
                {canAccessNotification() && (
                  <Dropdown.Item as={Link} to="/send-notification" className="sidebar-dropdown-item" onClick={handleNavClick("/send-notification")}>
                    Notifications
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </nav>
  );
};

export default Sidebar;
